import type {
  ConsentSdkCacheEntry,
  ConsentSdkClientOptions,
  ConsentSdkConsentState,
  ConsentSdkPolicy,
  ConsentSdkPolicyVersion,
  ConsentSdkRecordInput,
  ConsentSdkTheme,
  ConsentSdkTranslatedContent,
} from "./consent-sdk.types";
import { resolveSdkLanguage } from "./ui-strings.ts";
import { applyTranslationOverlay } from "./apply-translation.ts";

const SDK_VERSION = "1.3.0";

export function privacyTokenKey(appId: string): string {
  return `aithoris_privacy_token:${appId}`;
}

export function readPrivacyToken(appId: string): string | null {
  if (typeof sessionStorage === "undefined") return null;
  return (
    sessionStorage.getItem(privacyTokenKey(appId)) ??
    sessionStorage.getItem(`compally_privacy_token:${appId}`)
  );
}

export function writePrivacyToken(appId: string, token: string): void {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.setItem(privacyTokenKey(appId), token);
  sessionStorage.removeItem(`compally_privacy_token:${appId}`);
}

export function clearPrivacyToken(appId: string): void {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.removeItem(privacyTokenKey(appId));
  sessionStorage.removeItem(`compally_privacy_token:${appId}`);
}

export function needsReconsent(
  acceptedVersion: string | null | undefined,
  latestVersion: string | null | undefined,
): boolean {
  if (!latestVersion) return false;
  if (!acceptedVersion) return true;
  return String(acceptedVersion) !== String(latestVersion);
}

export function consentCacheKey(appId: string, principalId: string): string {
  return `aithoris_consent:${appId}:${principalId}`;
}

export function anonymousPrincipalKey(appId: string): string {
  return `aithoris_consent_anon:${appId}`;
}

export function readConsentCache(
  appId: string,
  principalId: string,
): ConsentSdkCacheEntry | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw =
      localStorage.getItem(consentCacheKey(appId, principalId)) ??
      localStorage.getItem(`compally_consent:${appId}:${principalId}`);
    if (!raw) return null;
    return JSON.parse(raw) as ConsentSdkCacheEntry;
  } catch {
    return null;
  }
}

export function writeConsentCache(
  appId: string,
  principalId: string,
  entry: Omit<ConsentSdkCacheEntry, "cachedAt" | "principalId">,
): void {
  if (typeof localStorage === "undefined") return;
  const payload: ConsentSdkCacheEntry = {
    ...entry,
    principalId,
    cachedAt: new Date().toISOString(),
  };
  localStorage.setItem(consentCacheKey(appId, principalId), JSON.stringify(payload));
  localStorage.removeItem(`compally_consent:${appId}:${principalId}`);
}

export function clearConsentCache(appId: string, principalId: string): void {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(consentCacheKey(appId, principalId));
  localStorage.removeItem(`compally_consent:${appId}:${principalId}`);
}

async function parseJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const message =
      typeof body === "object" && body && "message" in body
        ? Array.isArray((body as { message: unknown }).message)
          ? String(((body as { message: unknown[] }).message)[0])
          : String((body as { message: unknown }).message)
        : `Request failed (${response.status})`;
    throw new Error(message);
  }
  return response.json() as Promise<T>;
}

export class ConsentSdkClient {
  readonly apiBaseUrl: string;
  readonly appId: string;
  readonly sdkVersion: string;
  private readonly fetchImpl: typeof fetch;
  private readonly policyId?: string;
  private readonly policyVersion?: string;
  private policyEtag: string | null = null;
  private cachedPolicy: ConsentSdkPolicy | null = null;

  constructor(options: ConsentSdkClientOptions) {
    // Empty apiBaseUrl is valid with Vite `/api` proxy (same-origin).
    this.apiBaseUrl = (options.apiBaseUrl ?? "").replace(/\/$/, "");
    this.appId = options.appId;
    this.sdkVersion = options.sdkVersion ?? SDK_VERSION;
    this.fetchImpl = options.fetch ?? fetch.bind(globalThis);
    this.policyId = options.policyId;
    this.policyVersion = options.policyVersion;
  }

  private headers(method: string, extra?: HeadersInit): HeadersInit {
    const headers: Record<string, string> = {
      "X-Aithoris-SDK-Version": this.sdkVersion,
    };
    // Avoid Content-Type on GET — it can force unnecessary preflights.
    if (method !== "GET" && method !== "HEAD") {
      headers["Content-Type"] = "application/json";
    }
    return { ...headers, ...(extra as Record<string, string> | undefined) };
  }

  private url(path: string): string {
    const base = `${this.apiBaseUrl}/api/consent/public/apps/${this.appId}${path}`;
    if (
      (path === "/policy" || path.startsWith("/policy?")) &&
      this.policyId &&
      this.policyVersion
    ) {
      const q = new URLSearchParams({
        policyId: this.policyId,
        version: this.policyVersion,
      });
      return `${this.apiBaseUrl}/api/consent/public/apps/${this.appId}/policy?${q.toString()}`;
    }
    return base;
  }

  private async request(path: string, init?: RequestInit): Promise<Response> {
    const method = (init?.method ?? "GET").toUpperCase();
    return this.fetchImpl(this.url(path), {
      ...init,
      method,
      // Always bypass HTTP cache so browser If-None-Match does not yield empty 304s
      // that leave the in-memory SDK cache empty and break notice load.
      cache: "no-store",
      headers: this.headers(method, init?.headers),
    });
  }

  /** Bundled website + policy + branding config (preferred SDK entry). */
  async getConfig(opts?: { language?: string }): Promise<ConsentSdkPolicy> {
    const res = await this.fetchImpl(
      `${this.apiBaseUrl}/api/consent/public/config/${encodeURIComponent(this.appId)}`,
      {
        method: "GET",
        cache: "no-store",
        headers: this.headers("GET"),
      },
    );
    const cfg = await parseJson<{
      siteId: string;
      website: { name: string; domain?: string | null };
      branding?: ConsentSdkTheme;
      theme?: ConsentSdkTheme;
      policy: {
        policyId: string;
        version: string;
        title: string | null;
        notice: string | null;
        bannerHeadline?: string | null;
        updatedAt: string;
        purposes?: ConsentSdkPolicy["purposes"] | null;
        withdrawal?: { description?: string } | null;
      } | null;
      purposes: ConsentSdkPolicy["purposes"];
      banner?: { headline?: string | null; body?: string | null; position?: string };
      localization?: {
        languages?: string[];
        enabledLanguages?: string[];
        defaultLanguage?: string;
        multilingualEnabled?: boolean;
        translations?: Record<string, ConsentSdkTranslatedContent>;
      };
      cookieCatalog?: ConsentSdkPolicy["cookieCatalog"];
      bannerOptions?: ConsentSdkPolicy["bannerOptions"];
    }>(res);

    if (!cfg.policy) {
      throw new Error("No published policy for this site");
    }

    const languages = Array.isArray(cfg.localization?.languages)
      ? cfg.localization!.languages!
      : ["en"];
    const enabledLanguages = Array.isArray(cfg.localization?.enabledLanguages)
      ? cfg.localization!.enabledLanguages!
      : languages;
    const multilingualEnabled = Boolean(cfg.localization?.multilingualEnabled);
    const translations = cfg.localization?.translations ?? {};
    /**
     * Selector = ready languages only (server `languages` + translation keys).
     * Do NOT prefer enabledLanguages — unready langs must stay hidden.
     */
    const selectorLanguages = [
      ...new Set([
        "en",
        ...languages.filter((l) => l === "en" || Boolean(translations[l])),
        ...Object.keys(translations).filter((l) => l && l !== "en"),
      ]),
    ];
    const defaultLanguage = cfg.localization?.defaultLanguage ?? "en";
    const languageSelected = resolveSdkLanguage(
      opts?.language,
      selectorLanguages,
      defaultLanguage,
    );

    const masterPurposes = Array.isArray(cfg.purposes) ? cfg.purposes : [];
    const masterTitle = cfg.policy.title;
    const masterNotice = cfg.policy.notice;
    const masterBannerHeadline =
      cfg.banner?.headline ?? cfg.policy.bannerHeadline ?? cfg.theme?.bannerHeadline ?? null;
    const masterBannerBody = cfg.banner?.body ?? cfg.theme?.bannerBody ?? masterNotice;

    const base: ConsentSdkPolicy = {
      app: { id: cfg.siteId, name: cfg.website.name },
      policyId: cfg.policy.policyId,
      version: cfg.policy.version,
      title: masterTitle,
      notice: masterNotice,
      purposes: masterPurposes,
      updatedAt: cfg.policy.updatedAt,
      theme: {
        ...(cfg.theme ?? {}),
        ...(cfg.branding ?? {}),
        bannerHeadline: masterBannerHeadline ?? undefined,
        bannerBody: masterBannerBody ?? undefined,
        position: (cfg.banner?.position as ConsentSdkTheme["position"]) ?? cfg.theme?.position,
      },
      localization: {
        languages: selectorLanguages,
        enabledLanguages,
        defaultLanguage,
        multilingualEnabled,
        translations,
        masterTitle,
        masterNotice,
        masterPurposes,
        masterBannerHeadline,
        masterBannerBody,
      },
      languageSelected: "en",
      cookieCatalog: cfg.cookieCatalog ?? null,
      bannerOptions: cfg.bannerOptions ?? null,
    };

    const policy = applyTranslationOverlay(base, languageSelected);
    this.cachedPolicy = policy;
    return policy;
  }

  async getPolicy(opts?: { etag?: string | null; force?: boolean }): Promise<{
    policy: ConsentSdkPolicy | null;
    etag: string | null;
    notModified: boolean;
  }> {
    const etag = opts?.force ? null : (opts?.etag ?? this.policyEtag);
    const res = await this.request("/policy", {
      headers: etag ? { "If-None-Match": etag } : undefined,
    });

    // Browser/proxy 304 with no in-memory body → force a full reload once.
    if (res.status === 304) {
      if (this.cachedPolicy) {
        return { policy: this.cachedPolicy, etag: this.policyEtag, notModified: true };
      }
      const retry = await this.request("/policy");
      if (retry.status === 304) {
        throw new Error("Policy not available (empty cache revalidation)");
      }
      const raw = await parseJson<ConsentSdkPolicy>(retry);
      const policy = this.normalizePolicyLocalization(raw);
      this.cachedPolicy = policy;
      this.policyEtag = retry.headers.get("ETag");
      return { policy, etag: this.policyEtag, notModified: false };
    }

    const raw = await parseJson<ConsentSdkPolicy>(res);
    const policy = this.normalizePolicyLocalization(raw);
    this.cachedPolicy = policy;
    this.policyEtag = res.headers.get("ETag");
    return { policy, etag: this.policyEtag, notModified: false };
  }

  /** Ensure master snapshot + language list exist for client-side switching. */
  private normalizePolicyLocalization(raw: ConsentSdkPolicy): ConsentSdkPolicy {
    const loc = raw.localization ?? {
      languages: ["en"],
      defaultLanguage: "en",
      multilingualEnabled: false,
      translations: {},
    };
    const translations = loc.translations ?? {};
    const readyFromMap = Object.keys(translations).filter((l) => l && l !== "en");
    const serverLanguages =
      Array.isArray(loc.languages) && loc.languages.length ? loc.languages : ["en"];
    // Ready-only: intersection of advertised languages with present translation objects (+ en).
    const languages = [
      ...new Set(
        serverLanguages
          .map((l) => l.toLowerCase())
          .filter((l) => l === "en" || Boolean(translations[l])),
      ),
    ];
    if (!languages.includes("en")) languages.unshift("en");
    for (const code of readyFromMap) {
      if (!languages.includes(code)) languages.push(code);
    }
    const enabledLanguages =
      Array.isArray(loc.enabledLanguages) && loc.enabledLanguages.length
        ? loc.enabledLanguages
        : languages;
    const multilingualEnabled = Boolean(loc.multilingualEnabled);
    const withMaster: ConsentSdkPolicy = {
      ...raw,
      localization: {
        ...loc,
        languages,
        enabledLanguages,
        defaultLanguage: loc.defaultLanguage ?? "en",
        multilingualEnabled,
        translations,
        masterTitle: loc.masterTitle ?? raw.title,
        masterNotice: loc.masterNotice ?? raw.notice,
        masterPurposes: loc.masterPurposes ?? raw.purposes,
        masterBannerHeadline: loc.masterBannerHeadline ?? raw.theme?.bannerHeadline ?? null,
        masterBannerBody: loc.masterBannerBody ?? raw.theme?.bannerBody ?? raw.notice ?? null,
      },
      languageSelected: raw.languageSelected ?? "en",
    };
    return applyTranslationOverlay(
      withMaster,
      resolveSdkLanguage(undefined, languages, withMaster.localization!.defaultLanguage),
    );
  }

  async getPolicyVersion(): Promise<ConsentSdkPolicyVersion> {
    const res = await this.request("/policy/version");
    return parseJson(res);
  }

  async getConsent(principalId: string): Promise<ConsentSdkConsentState> {
    const res = await this.request(`/consent?principalId=${encodeURIComponent(principalId)}`);
    const state = await parseJson<ConsentSdkConsentState>(res);
    writeConsentCache(this.appId, principalId, {
      policyVersion: state.policyVersion,
      recordId: state.recordId,
      purposes: state.purposes,
    });
    return state;
  }

  /**
   * Server is source of truth. localStorage is a hint only —
   * always revalidate against the API when possible.
   */
  async resolveConsent(principalId: string): Promise<{
    server: ConsentSdkConsentState | null;
    cache: ConsentSdkCacheEntry | null;
    needsUi: boolean;
    latestVersion: string | null;
  }> {
    const versionInfo = await this.getPolicyVersion();
    const cache = readConsentCache(this.appId, principalId);
    let server: ConsentSdkConsentState | null = null;
    try {
      server = await this.getConsent(principalId);
    } catch {
      server = null;
    }
    const acceptedVersion = server?.policyVersion ?? cache?.policyVersion ?? null;
    const needsUi =
      !server ||
      !server.isActive ||
      needsReconsent(acceptedVersion, versionInfo.version);
    return {
      server,
      cache,
      needsUi,
      latestVersion: versionInfo.version,
    };
  }

  async recordConsent(input: ConsentSdkRecordInput): Promise<ConsentSdkConsentState> {
    const res = await this.request("/consent", {
      method: "POST",
      body: JSON.stringify(input),
    });
    const state = await parseJson<ConsentSdkConsentState>(res);
    writeConsentCache(this.appId, state.principalId, {
      policyVersion: state.policyVersion,
      recordId: state.recordId,
      purposes: state.purposes,
    });
    if (state.anonymous) {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(anonymousPrincipalKey(this.appId), state.principalId);
      }
    }
    return state;
  }

  async withdraw(input: {
    recordId?: string;
    principalId?: string;
  }): Promise<unknown> {
    const res = await this.request("/withdraw", {
      method: "POST",
      body: JSON.stringify(input),
    });
    const result = await parseJson(res);
    if (input.principalId) clearConsentCache(this.appId, input.principalId);
    return result;
  }

  async mergeAnonymous(anonymousPrincipalId: string, principalId: string) {
    const res = await this.request("/consent/merge", {
      method: "POST",
      body: JSON.stringify({ anonymousPrincipalId, principalId }),
    });
    const state = await parseJson<ConsentSdkConsentState & { mergedFrom?: string }>(res);
    clearConsentCache(this.appId, anonymousPrincipalId);
    writeConsentCache(this.appId, principalId, {
      policyVersion: state.policyVersion,
      recordId: state.recordId,
      purposes: state.purposes ?? {},
    });
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(anonymousPrincipalKey(this.appId));
    }
    return state;
  }

  getStoredAnonymousPrincipal(): string | null {
    if (typeof localStorage === "undefined") return null;
    return (
      localStorage.getItem(anonymousPrincipalKey(this.appId)) ??
      localStorage.getItem(`compally_consent_anon:${this.appId}`)
    );
  }

  // --- C13 Privacy Dashboard (principal JWT) ---

  private privacyUrl(path: string): string {
    return this.url(`/privacy${path}`);
  }

  private async privacyRequest(path: string, init?: RequestInit): Promise<Response> {
    const method = (init?.method ?? "GET").toUpperCase();
    const token = readPrivacyToken(this.appId);
    const headers: Record<string, string> = {
      ...(this.headers(method, init?.headers) as Record<string, string>),
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    return this.fetchImpl(this.privacyUrl(path), {
      ...init,
      method,
      cache: "no-store",
      headers,
    });
  }

  async requestOtp(principalId: string, identifierType = "email") {
    const res = await this.privacyRequest("/auth/request-otp", {
      method: "POST",
      body: JSON.stringify({ principalId, identifierType }),
    });
    return parseJson(res);
  }

  async loginWithOtp(principalId: string, otp: string, identifierType = "email") {
    const res = await this.privacyRequest("/auth/login", {
      method: "POST",
      body: JSON.stringify({ principalId, otp, identifierType }),
    });
    const data = await parseJson<{ token: string }>(res);
    writePrivacyToken(this.appId, data.token);
    return data;
  }

  logoutPrivacy() {
    clearPrivacyToken(this.appId);
  }

  async getPrivacyDashboard() {
    const res = await this.privacyRequest("/dashboard");
    return parseJson(res);
  }

  async getConsentHistory() {
    const res = await this.privacyRequest("/history");
    return parseJson(res);
  }

  async getActivityTimeline() {
    const res = await this.privacyRequest("/activity");
    return parseJson(res);
  }

  async getRequests() {
    const res = await this.privacyRequest("/requests");
    return parseJson(res);
  }

  async getRequestStatus(requestId: string) {
    const res = await this.privacyRequest(`/requests/${encodeURIComponent(requestId)}`);
    return parseJson(res);
  }

  async updatePreferences(body: {
    policyId: string;
    policyVersion: string;
    dataPointConsents: Array<{
      data_point_id: string;
      consent_granted: boolean;
      purpose_agreed_to?: string;
    }>;
    languageSelected?: string;
  }) {
    const res = await this.privacyRequest("/update-preferences", {
      method: "POST",
      body: JSON.stringify(body),
    });
    return parseJson(res);
  }

  async requestAccountDeletion(body?: { subject?: string; description?: string }) {
    const res = await this.privacyRequest("/delete-account", {
      method: "POST",
      body: JSON.stringify(body ?? {}),
    });
    return parseJson(res);
  }

  async requestDataExport(body?: { subject?: string; description?: string }) {
    const res = await this.privacyRequest("/export", {
      method: "POST",
      body: JSON.stringify(body ?? {}),
    });
    return parseJson(res);
  }

  async raiseComplaint(body: { subject: string; description: string; type?: string }) {
    const res = await this.privacyRequest("/grievance", {
      method: "POST",
      body: JSON.stringify(body),
    });
    return parseJson(res);
  }

  async requestCorrection(body?: { subject?: string; description?: string }) {
    const res = await this.privacyRequest("/correction", {
      method: "POST",
      body: JSON.stringify(body ?? {}),
    });
    return parseJson(res);
  }

  async withdrawConsent(recordId?: string) {
    const res = await this.privacyRequest("/withdraw", {
      method: "POST",
      body: JSON.stringify(recordId ? { recordId } : {}),
    });
    return parseJson(res);
  }

  async getPrivacyNotifications() {
    const res = await this.privacyRequest("/notifications");
    return parseJson(res);
  }

  async markPrivacyNotificationRead(id: string) {
    const res = await this.privacyRequest(`/notifications/${encodeURIComponent(id)}/read`, {
      method: "POST",
    });
    return parseJson(res);
  }
}

export { SDK_VERSION as COMPALLY_CONSENT_SDK_VERSION };
